import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  IconButton,
  Tooltip,
  List,
  MenuItem
} from '@mui/material';
import { styled } from '@mui/system';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { Link } from 'react-router-dom';
import { useSelector, shallowEqual } from 'react-redux';
import { renderDate } from '#component/form/select';
import { fetchLimitedProjects } from '#action/project/dashboard';

const StyledDialogTitle = styled(DialogTitle)(() => ({
  padding: '16px 24px 0'
}));

const StyledDialogContent = styled(DialogContent)(() => ({
  paddingTop: 0,
  paddingBottom: 0
}));

const DialogContentList = styled(List)(() => ({
  paddingBottom: 0
}));

const DotsContainer = styled('div')(() => ({
  fontSize: 10
}));

const DotIcon = styled(FiberManualRecordIcon)(({ theme }) => ({
  color: theme.palette.grey[600],
  margin: '0 2px'
}));

const DotButton = styled(IconButton)(() => ({
  paddingTop: 4,
  '&:hover': {
    borderRadius: 4
  }
}));

const DotsContainerPopup = styled('div')(() => ({
  fontSize: 10,
  height: 10,
  marginRight: 4
}));

const StyledMenuItem = styled(MenuItem)(() => ({
  paddingLeft: 0,
  paddingRight: 0,
  display: 'flex',
  flexWrap: 'wrap',
  whiteSpace: 'normal'
}));

const renderItem = (data, html = [], account) => {
  data.forEach((f, j) => {
    html.push(
      <StyledMenuItem
        component={Link}
        to={`/project/edit/${f.id}`}
        key={f.id}
        value={f.id}
        disabled={j === 0}
      >
        <DotsContainerPopup>
          {f.ancestors && f.ancestors.map((a, i) =>
            <DotIcon
              key={i}
              fontSize="inherit"
            />) }
        </DotsContainerPopup>
        <span style={{ marginRight: 8 }}>{f.title}</span>
        {renderDate(f, account)}
      </StyledMenuItem>
    );
  });
  return html;
};

/**
 * Renders IconButton with dialog with all projects related to device.
 * @returns {React.FC}
 */

const ProjectDots = props => {
  const {
    project: {
      ancestors = []
    },
    project
  } = props;
  const [open, setOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const account = useSelector(s => s.account, shallowEqual);
  const projectIds = ancestors.map(a => a.id).join(',');

  const handleShowProjectsClick = async event => {
    event.stopPropagation();
    const projectsRes = await fetchLimitedProjects(projectIds, { modules: false }) || [];

    setOpen(true);
    setProjects([...projectsRes, project]);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDialogClick = event => {
    event.stopPropagation();
  };

  const handleDialogMouseDown = event => {
    event.stopPropagation();
  };

  return (
    <>
      <DotButton
        size="small"
        aria-label="Details"
        color="inherit"
        onClick={handleShowProjectsClick}
      >
        <DotsContainer>
          {ancestors && ancestors.map((el, i) =>
            <Tooltip title={`${el?.title}`} key={i}>
              <DotIcon
                fontSize="inherit"
              />
            </Tooltip>)}
        </DotsContainer>
      </DotButton>
      <Dialog
        open={open}
        onClose={handleClose}
        onClick={handleDialogClick}
        onMouseDown={handleDialogMouseDown}
      >
        <StyledDialogTitle>
          Hierarchy
        </StyledDialogTitle>
        <StyledDialogContent>
          <DialogContentList>
            {renderItem(projects, [], account)}
          </DialogContentList>
        </StyledDialogContent>
        <DialogActions>
          <Button variant="outlined" color="primary" onClick={handleClose}>
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default ProjectDots;
